import { defineStore } from 'pinia'
import { useRequest }               from 'composables/useRequest.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

// Module-scoped so it stays out of reactive state — only ever compared, and a
// slow in-flight search must not be allowed to overwrite a newer one's results.
let _searchToken = 0

// Range key -> in-flight promise, so overlapping mounts share one request.
const _inFlightRanges = new Map()

// Ranges are ISO date strings, so lexicographic comparison is chronological.
// Without merging, a session that pages through weeks accumulated one entry per
// week forever and isRangeLoaded scanned all of them on every fetch.
function mergeRanges(ranges, next) {
  const all = [...ranges, next].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0))
  const merged = []

  for (const range of all) {
    const last = merged[merged.length - 1]
    // Adjacent counts as overlapping: consecutive weeks leave no gap.
    if (last && range.start <= nextDay(last.end)) {
      if (range.end > last.end) last.end = range.end
    } else {
      merged.push({ ...range })
    }
  }
  return merged
}

function nextDay(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().split('T')[0]
}

export const useCalendarStore = defineStore('calendar', {
  state: () => ({
    // Bookings indexed by id for O(1) real-time mutations
    bookingsById: {},

    // Track which date ranges have been loaded to avoid double-fetching
    // Array of { start: ISOString, end: ISOString }
    loadedRanges: [],

    // Tasks overlaid on the grid, Google-Calendar-Tasks style — separate
    // cache from bookings (different API, different shape) but same
    // range-loading strategy.
    tasksById: {},
    loadedTaskRanges: [],
    showTasksOnCalendar: localStorage.getItem('calendar_show_tasks') !== 'false',
    tasksLoading: false,
    tasksError: null,

    // Host events (booking type list for the create-booking form)
    hostEvents: [],
    hostEventsLoadedAt: null,

    // Connected calendars (sidebar list with color pickers)
    connectedCalendars: [],
    connectedCalendarsLoadedAt: null,

    // UI state
    visibleCalendarIds: [],       // array of calendar IDs currently shown (not a Set — Pinia can't serialize Sets)
    calendarVisibilityInitialized: false, // until the first load, an empty list means "not known yet", not "none"

    selectedEventFilter: 'all',   // 'all' or an event_id string
    currentView: 'week',          // 'day' | 'week' | 'month' | '4days' | 'year'
    viewInitialized: false,        // true after default_view from settings has been applied once
    selectedDate: null,           // null means today

    // Slide-over panels
    settingsPanelOpen: false,
    bookingSlideOverOpen: false,
    bookingSlideOverBooking: null,  // null = create mode, object = edit mode
    bookingSlideOverSlotDate: null,    // clicked/dragged grid slot, pre-fills the create form
    bookingSlideOverSlotEndDate: null, // set when the slot came from a drag rather than a click

    // Which day's task list is expanded, if any. Held centrally so opening one
    // closes any other: each summary owning its own flag meant clicking a second
    // day left the first list hanging open, because the button stops the click
    // before it can reach the other instances.
    openTaskListId: null,

    // Event popover
    eventPopoverOpen: false,
    eventPopoverBooking: null,
    eventPopoverAnchor: null,  // { top, left, right, bottom, width, height } from getBoundingClientRect

    // Full search results overlay (opened by pressing Enter in the header
    // search box) — replaces the calendar grid with a Google-Calendar-style
    // chronological list, as opposed to the small proximity-sorted autofill
    // dropdown shown while typing.
    searchOverlayOpen: false,
    searchOverlayQuery: '',
    searchOverlayResults: [],
    searchOverlayLoading: false,

    // Where overlays (popover, slide-over, settings panel) teleport to.
    // Normally 'body'; switched to the fullscreen element while native
    // fullscreen is active, because content outside it is not rendered.
    overlayTarget: 'body',

    // ActionCable connection status
    connectionStatus: 'connecting', // 'connected' | 'disconnected' | 'reconnecting'

    // Loading / error
    bookingsLoading: false,
    bookingsError: null,
    hostEventsLoading: false,
    hostEventsError: null,
    connectedCalendarsLoading: false,
  }),

  getters: {
    bookings: (state) => Object.values(state.bookingsById),

    filteredBookings: (state) => {
      const settings = useCalendarSettingsStore()
      const now      = new Date()

      let bookings = Object.values(state.bookingsById).filter(b => {
        // An empty visibleCalendarIds means the user unchecked everything, so
        // nothing external should show. Before the first calendars load we have
        // no list yet, and everything shows.
        const calendarVisible =
          !b.calendar_id ||
          !state.calendarVisibilityInitialized ||
          state.visibleCalendarIds.includes(b.calendar_id)
        const eventVisible =
          state.selectedEventFilter === 'all' ||
          b.event_id === state.selectedEventFilter
        return calendarVisible && eventVisible
      })

      if (!settings.show_past_bookings) {
        bookings = bookings.filter(b => new Date(b.end_at ?? b.end) > now)
      }

      // A cancel arrives over ActionCable as booking_updated (bookings are
      // status-flipped, not destroyed), so the grid has to drop them here too.
      bookings = bookings.filter(b => b.status !== 'canceled' && b.status !== 'rescheduled')

      if (!settings.show_declined_bookings) {
        bookings = bookings.filter(b => b.status !== 'declined')
      }

      if (settings.collapse_duplicate_events) {
        const seen = new Set()
        bookings = bookings.filter(b => {
          const key = `${b.title ?? b.name}|${b.start_at ?? b.start}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      }

      return bookings
    },

    isRangeLoaded: (state) => (start, end) => {
      return state.loadedRanges.some(r => r.start <= start && r.end >= end)
    },

    tasks: (state) => Object.values(state.tasksById),

    // Only surfaces doable tasks with a due date — canceled ones stay
    // hidden on the grid the same way canceled bookings would.
    filteredTasks: (state) => {
      if (!state.showTasksOnCalendar) return []
      return Object.values(state.tasksById).filter(t => t.due_at && t.status !== 'canceled')
    },

    isTaskRangeLoaded: (state) => (start, end) => {
      return state.loadedTaskRanges.some(r => r.start <= start && r.end >= end)
    },

    hostEventsIsFresh: (state) =>
      state.hostEventsLoadedAt !== null &&
      Date.now() - state.hostEventsLoadedAt < CACHE_TTL,

    connectedCalendarsIsFresh: (state) =>
      state.connectedCalendarsLoadedAt !== null &&
      Date.now() - state.connectedCalendarsLoadedAt < CACHE_TTL,

    // Deactivated calendars/sub-calendars are never shown on the /calendar
    // page — the API returns everything, so this is filtered client-side.
    activeConnectedCalendars: (state) =>
      state.connectedCalendars
        .filter(c => c.active)
        .map(c => ({ ...c, sub_calendars: (c.sub_calendars || []).filter(s => s.active) })),
  },

  actions: {
    // ── Initial load ──────────────────────────────────────────────

    async fetchInitialData() {
      await Promise.all([
        this.fetchHostEventsIfStale(),
        this.fetchConnectedCalendarsIfStale(),
      ])
    },

    // ── Bookings ──────────────────────────────────────────────────

    async fetchBookings(start, end) {
      if (this.isRangeLoaded(start, end)) return

      // Two views mounting at once (or a fast week-to-week scrub) used to fire
      // duplicate requests for the same range and race on bookingsLoading.
      const key = `${start}:${end}`
      if (_inFlightRanges.has(key)) return _inFlightRanges.get(key)

      const request = this._fetchBookingsRequest(start, end, key)
      _inFlightRanges.set(key, request)
      return request
    },

    async _fetchBookingsRequest(start, end, key) {
      this.bookingsLoading = true
      this.bookingsError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest(
          'GET',
          `/api/v1/calendar/bookings?start_date=${start}&end_date=${end}`,
          {},
          false
        )
        if (!res.js_error && res.response?.success) {
          const bookings = res.response.data?.bookings ?? res.response.data
          if (Array.isArray(bookings)) {
            bookings.forEach(b => { this.bookingsById[b.id] = b })
          }
          this.loadedRanges = mergeRanges(this.loadedRanges, { start, end })
        } else {
          this.bookingsError = res.response?.message || 'Failed to load bookings.'
        }
      } catch {
        this.bookingsError = 'Something went wrong loading your calendar.'
      } finally {
        _inFlightRanges.delete(key)
        this.bookingsLoading = _inFlightRanges.size > 0
      }
    },

    // ── Drag / drop reschedule ────────────────────────────────────────
    //
    // Split into preview / commit / rollback so a recurring event can ask "this
    // event or all events?" while the chip stays where it was dropped. Applying
    // the move only after the dialog closed made the chip snap back and then
    // jump again, which reads as a glitch.

    // Optimistic move, no network. Returns a snapshot to roll back to.
    previewMove(booking, startAt, endAt, scope = 'this') {
      const current = this.bookingsById[booking.id]
      if (!current) return null

      const snapshot = { scope, bookings: {} }
      const shiftMs = new Date(startAt) - new Date(current.start_at ?? current.start)

      const apply = (b, nextStart, nextEnd) => {
        snapshot.bookings[b.id] = { ...b }
        this.bookingsById[b.id] = {
          ...b,
          start: nextStart, start_at: nextStart,
          end: nextEnd, end_at: nextEnd,
        }
      }

      apply(current, startAt, endAt)

      // "All events" moves every instance of the series by the same delta.
      if (scope === 'all' && current.recurring_booking_id) {
        Object.values(this.bookingsById).forEach(b => {
          if (b.id === current.id) return
          if (b.recurring_booking_id !== current.recurring_booking_id) return
          if (b.calendar_id !== current.calendar_id) return
          const s = new Date(new Date(b.start_at ?? b.start).getTime() + shiftMs).toISOString()
          const e = new Date(new Date(b.end_at ?? b.end).getTime() + shiftMs).toISOString()
          apply(b, s, e)
        })
      }

      return snapshot
    },

    rollbackMove(snapshot) {
      if (!snapshot) return
      Object.values(snapshot.bookings).forEach(b => { this.bookingsById[b.id] = b })
    },

    // Persists a move already applied by previewMove.
    async commitMove(booking, startAt, endAt, { scope = 'this', notify = 'none', snapshot = null } = {}) {
      const id = booking.id
      // Timelydo bookings and mirrored provider events live in different tables
      // behind different endpoints; `context` says which.
      const isExternal = booking.context === 'ExternalBooking'
      const path = isExternal
        ? `/api/v1/connected_calendar_bookings/${id}`
        : `/api/v1/bookings/${id}`

      const payload = {
        booking: {
          start_at: startAt,
          end_at: endAt,
          // The zone the grid was rendered in, which is the zone the wall-clock
          // times above are expressed in. The server must not guess: an event
          // synced from Google carries its organizer's timezone, not the
          // viewer's, and reading the drop time in that zone shifted every move
          // of a foreign-timezone event.
          timezone: useCalendarSettingsStore().date_primary_timezone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }
      if (isExternal) {
        Object.assign(payload, { scope, notify })
        // The row id above dies at the next Google sync, which replaces every
        // mirrored booking rather than updating it. A calendar left open across
        // a sync therefore drags a chip whose id no longer exists and got a 404.
        // These two are the event's durable identity, and the server falls back
        // to them when the id misses.
        Object.assign(payload.booking, {
          external_booking_id: booking.external_booking_id,
          calendar_id: booking.calendar_id,
        })
      }

      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('PUT', path, payload, false)

        if (!res.js_error && res.response?.success) {
          // Take the server's version back: Google can normalise times and the
          // payload carries fields the optimistic patch could not know.
          if (res.response.data?.id) {
            // The server may have resolved the booking by its Google identity
            // after a sync reissued the row id. That returns a different id than
            // the one the grid is holding, so retire the old key or the event
            // renders twice.
            if (res.response.data.id !== id) this.removeBooking(id)
            this.updateBooking(res.response.data)
          }

          // A series move only returns the dragged instance, but it changed
          // every occurrence. The instances already on screen keep their
          // optimistic shift so the grid does not flicker; dropping the loaded
          // ranges makes the next navigation re-fetch the real times rather
          // than trusting that shift indefinitely.
          if (scope === 'all') this.loadedRanges = []

          return { ok: true, snapshot }
        }
        this.rollbackMove(snapshot)
        return { ok: false, error: res.response?.message || 'Could not move this booking.' }
      } catch {
        this.rollbackMove(snapshot)
        return { ok: false, error: 'Something went wrong moving this booking.' }
      }
    },

    // Real-time mutations — called by the ActionCable composable
    addBooking(booking)    { this.bookingsById[booking.id] = booking },
    // Upsert, not patch-if-present: an event moved *into* the loaded range
    // arrives as an update for a booking the cache has never seen, and dropping
    // it meant the move was invisible until a refresh.
    updateBooking(booking) {
      if (!booking?.id) return
      this.bookingsById[booking.id] = { ...(this.bookingsById[booking.id] || {}), ...booking }
    },
    removeBooking(id)      { delete this.bookingsById[id] },

    updateBookingNotes(id, notes) {
      const b = this.bookingsById[id]
      if (b) b.internal_notes = notes
    },

    // A note was applied to a whole recurring series — patch every visible instance
    updateSeriesNotes(recurringBookingId, calendarId, notes) {
      if (!recurringBookingId) return
      Object.values(this.bookingsById).forEach(b => {
        if (b.recurring_booking_id === recurringBookingId && b.calendar_id === calendarId) {
          b.internal_notes = notes
        }
      })
    },

    // The owner's RSVP changed (Going? Yes/No/Maybe) — keep the cached booking
    // consistent so chip styling (stripes, strikethrough) updates immediately.
    updateBookingRsvp(id, status) {
      const b = this.bookingsById[id]
      if (!b) return
      b.calendar_owner_status = status
      const self = b.attendees?.find(a => a.self)
      if (self) self.response_status = status
    },

    invalidate() {
      this.loadedRanges = []
      this.bookingsById = {}
      this.bookingsError = null
      _inFlightRanges.clear()
    },

    // ── Tasks (Google-Calendar-Tasks-style overlay) ─────────────────

    async fetchTasks(start, end) {
      if (this.isTaskRangeLoaded(start, end)) return

      this.tasksLoading = true
      this.tasksError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks?start_date=${start}&end_date=${end}`, {}, false)
        if (!res.js_error && res.response?.success) {
          res.response.data.forEach(t => { this.tasksById[t.id] = t })
          this.loadedTaskRanges.push({ start, end })
        } else {
          this.tasksError = res.response?.message || 'Failed to load tasks.'
        }
      } catch {
        this.tasksError = 'Something went wrong loading your tasks.'
      } finally {
        this.tasksLoading = false
      }
    },

    updateTask(task) {
      if (task?.id) this.tasksById[task.id] = { ...this.tasksById[task.id], ...task }
    },

    // Ticking a task moves it between the pending and completed sections. The
    // move is applied before the request goes out so the animation runs
    // immediately; a rejected request puts the row back exactly where it was.
    async toggleTaskStatus(task) {
      const id = task?.id
      const previous = id && this.tasksById[id]
      if (!previous) return { ok: false }

      const snapshot = { ...previous }
      const next = previous.status === 'completed' ? 'pending' : 'completed'

      this.tasksById[id] = { ...previous, status: next }

      try {
        const { useTasksStore } = await import('stores/tasks.js')
        const updated = await useTasksStore().setStatus(snapshot, next)

        if (updated) {
          this.tasksById[id] = { ...this.tasksById[id], ...updated }
          return { ok: true }
        }
        this.tasksById[id] = snapshot
        return { ok: false, error: 'Could not update that task.' }
      } catch {
        this.tasksById[id] = snapshot
        return { ok: false, error: 'Something went wrong.' }
      }
    },

    removeTask(id) { delete this.tasksById[id] },

    toggleTasksVisibility() {
      this.showTasksOnCalendar = !this.showTasksOnCalendar
      localStorage.setItem('calendar_show_tasks', String(this.showTasksOnCalendar))
    },

    invalidateTasks() {
      this.loadedTaskRanges = []
      this.tasksById = {}
      this.tasksError = null
    },

    // ── Host events ───────────────────────────────────────────────

    async fetchHostEventsIfStale() {
      if (this.hostEventsIsFresh && this.hostEvents.length) return
      await this.fetchHostEvents()
    },

    async fetchHostEvents() {
      this.hostEventsLoading = true
      this.hostEventsError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/events', {}, false)
        if (!res.js_error && res.response?.success) {
          this.hostEvents = res.response.data
          this.hostEventsLoadedAt = Date.now()
        } else {
          this.hostEventsError = res.response?.message || 'Failed to load events.'
        }
      } catch {
        this.hostEventsError = 'Something went wrong.'
      } finally {
        this.hostEventsLoading = false
      }
    },

    // ── Connected calendars ───────────────────────────────────────

    async fetchConnectedCalendarsIfStale() {
      if (this.connectedCalendarsIsFresh && this.connectedCalendars.length) return
      await this.fetchConnectedCalendars()
    },

    async fetchConnectedCalendars() {
      this.connectedCalendarsLoading = true
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/connected_calendars', {}, false)
        if (!res.js_error && res.response?.success) {
          this.connectedCalendars = res.response.data
          this.connectedCalendarsLoadedAt = Date.now()
          // Initialize visibility: show all *active* calendars (parents + sub-calendars)
          // by default. Deactivated ones are deliberately left out so their bookings
          // stay hidden via the calendarVisible check in filteredBookings.
          if (!this.calendarVisibilityInitialized) {
            const allIds = []
            res.response.data.forEach(c => {
              if (!c.active) return
              allIds.push(c.id)
              if (c.sub_calendars) c.sub_calendars.forEach(s => { if (s.active) allIds.push(s.id) })
            })
            this.visibleCalendarIds = allIds
            this.calendarVisibilityInitialized = true
          }
        }
      } finally {
        this.connectedCalendarsLoading = false
      }
    },

    // ── UI actions ────────────────────────────────────────────────

    setView(view)              { this.currentView = view },
    setDate(date)              { this.selectedDate = date },
    setConnectionStatus(s)     { this.connectionStatus = s },
    setEventFilter(id)         { this.selectedEventFilter = id || 'all' },

    toggleCalendarVisibility(id) {
      const idx = this.visibleCalendarIds.indexOf(id)
      if (idx === -1) {
        this.visibleCalendarIds.push(id)
      } else {
        this.visibleCalendarIds.splice(idx, 1)
      }
    },

    openSettingsPanel()  { this.settingsPanelOpen = true },
    closeSettingsPanel() { this.settingsPanelOpen = false },

    setOverlayTarget(target) { this.overlayTarget = target || 'body' },

    openTaskList(id)  { this.openTaskListId = id },
    closeTaskList()   { this.openTaskListId = null },
    toggleTaskList(id) {
      this.openTaskListId = this.openTaskListId === id ? null : id
    },

    openEventPopover(booking, anchorRect) {
      // An event popover and a task list should not sit open together.
      this.closeTaskList()
      this.eventPopoverBooking = booking
      this.eventPopoverAnchor  = anchorRect
        ? { top: anchorRect.top, left: anchorRect.left, right: anchorRect.right, bottom: anchorRect.bottom, width: anchorRect.width, height: anchorRect.height }
        : null
      this.eventPopoverOpen = true
    },
    closeEventPopover() {
      this.eventPopoverOpen   = false
      this.eventPopoverBooking = null
      this.eventPopoverAnchor  = null
    },

    openBookingSlideOver(booking = null, slotDate = null, slotEndDate = null) {
      this.bookingSlideOverBooking = booking
      this.bookingSlideOverSlotDate = slotDate
      this.bookingSlideOverSlotEndDate = slotEndDate
      this.bookingSlideOverOpen = true
    },
    closeBookingSlideOver() {
      this.bookingSlideOverOpen = false
      this.bookingSlideOverBooking = null
      this.bookingSlideOverSlotDate = null
      this.bookingSlideOverSlotEndDate = null
    },

    async openSearchOverlay(query, { from_date, to_date } = {}) {
      const token = ++_searchToken
      this.searchOverlayOpen = true
      this.searchOverlayQuery = query
      this.searchOverlayLoading = true
      try {
        const { axiosRequest } = useRequest()
        const params = new URLSearchParams({ query, mode: 'full' })
        if (from_date) params.set('from_date', from_date)
        if (to_date) params.set('to_date', to_date)
        const res = await axiosRequest('GET', `/api/v1/calendar/bookings/search?${params.toString()}`, {}, false)
        if (token !== _searchToken) return // a newer search superseded this one
        this.searchOverlayResults = (!res.js_error && res.response?.success) ? res.response.data : []
      } finally {
        if (token === _searchToken) this.searchOverlayLoading = false
      }
    },
    closeSearchOverlay() {
      this.searchOverlayOpen = false
      this.searchOverlayQuery = ''
      this.searchOverlayResults = []
    },
  },
})
